import React, { Component, Profiler } from "react";
import { toast } from "react-toastify";
import { DEV, getConfig } from "./configMgr";
import { Buffer } from "buffer";
export { RE_CATEGORY_PREFIXED_ID, getCategoryKey, getExternalID, getBestName } from "./entityIds";
import { RE_CATEGORY_PREFIXED_ID, getCategoryKey, getExternalID, getBestName } from "./entityIds";
export { RE_PLEX_ID, safeStringifyJSON, captureMessage, stylizePublications, linkifyPlexIds, readableIdTitle } from "./plexText";
import { RE_PLEX_ID } from "./plexText";

//const UNICODE_PUNCTUATION_RE = /[\u2000-\u206F\u2E00-\u2E7F\\'!"#$%()*+,\-./:;=?@[\]^_`{|}~]/g;
export const RE_TERM_LABEL = /\s+label\s*[:=]\s*/;

export const DELETE_MARKER = "ⓧ";
export const NBSP = "\u00A0";
export const TS = "\n";
export const {legacyGS, legacyGPSA} = getConfig();

export const elideString = (str, len, end = "...") => {
  return str.length >= len + end.length ? str.substring(0, len) + end : str;
};

// Smart truncation that breaks at word boundaries (space, closing brace/parens)
// Looks for the nearest word break before maxLen, falling back to hard truncation if needed
export const smartTruncate = (text, maxLen) => {
  if (!text || text.length <= maxLen) {
    return text;
  }

  // Try to find a word break character (space, ), }, ]) near the truncation point
  // Search backwards from maxLen to find the last word break
  const searchStart = Math.max(0, maxLen - 10); // Look within 10 chars before maxLen
  let breakPoint = -1;

  for (let i = maxLen - 1; i >= searchStart; i--) {
    const char = text[i];
    if (char === ' ' || char === ')' || char === '}' || char === ']') {
      breakPoint = i;
      break;
    }
  }

  // If we found a good break point, use it; otherwise use hard truncation
  if (breakPoint > searchStart) {
    return text.substring(0, breakPoint) + "...";
  } else {
    return text.substring(0, maxLen) + "...";
  }
};

export const saveBlobContent = (blobContent, filename) => {
  const blob = new Blob([blobContent], {type: blobContent.type});
  saveBlob(blob, filename);
};

export const formatTimestamp = (ts, full = false) => {
  // Timestamp is in milliseconds
  if (!ts) {
    return DEV ? `<ts=${ts}>` : "";
  }
  if (ts < 10000000000) {
    const ms = ts % 1000;
    const ss = Math.floor(ts / 1000) % 60;
    const mm = Math.floor(ts / 1000 / 60) % 60;
    const hh = Math.floor(ts / 1000 / 3600);
    return hh ? hh < 10 ? `${hh}h${mm.toString().padStart(2, '0')}m`
                        : `${hh}h`
              : mm ? mm < 10 ? `${mm}m${ss.toString().padStart(2, '0')}s`
                             : `${mm}m`
                   : ss < 10 ? `${Math.floor(ss)}.${ms.toString().padStart(3, '0')}s`
                             : `${ss}s`;
  }
  const date = new Date(ts);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");
  return full ? `${year}${month}${day}T${hour}:${minute}:${second}` : `${hour}:${minute}:${second}`;
};

export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  // navigator.msSaveOrOpenBlob - available for IE only
  if (navigator.msSaveOrOpenBlob) {
    navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const a = document.createElement("a");
    a.setAttribute("style", "display: none");
    a.setAttribute("target", "_black");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
};

export const safeDOMID = id => {
  return id?.replace(/[^-A-Z0-9_]/gi, "-").replace(/-{2,}/, "-");
};

export const wait = async (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const isNumericID = id => {
  return /^[A-Z]{2,}[-:0-9]{3,}$/i.test(id);
}

export const isInchi = id => {
  return /^((InChI=)[^J][0-9BCOHNSPrIFla+\-()\\/,pqbtmsih]{6,})$/i.test(id);
}

export const isInchiKey = id => {
  return /^[A-Z]{{14}}-[A-Z]{{10}}-[A-Z]?/.test(id);
}

// smiles:/query: IDs may contain backslashes and other characters excluded from RE_PLEX_ID
const RE_SMILES_QUERY_ID = /^(?:smiles|query):.+$/;
export const isPlexId = id => {
  return (RE_PLEX_ID.test(id) || RE_SMILES_QUERY_ID.test(id)) && !RE_CATEGORY_PREFIXED_ID.test(id);
}

export const extractLabel = term => {
  if (RE_TERM_LABEL.test(term)) {
    const [id, label] = term.split(RE_TERM_LABEL);
    return [id.trim(), label.trim()];
  }
  return [term , null];
};

export const saveCustomLabel = (id, label) => {
  try {
    const key = `label-${id}`;
    if (label) {
      localStorage[key] = label.trim();
    } else {
      localStorage.removeItem(key);
    }
  }
  catch(e) {
    console.error(`Could not access local storage ${e}`);
  }
};

export const getCustomLabels = () => {
  return Object.keys(localStorage)
    .filter(key => key.startsWith("label-"))
    .reduce((result, key) => {
      const id = key.substring("label-".length);
      result[id] = localStorage.getItem(key);
      return result;
    }, {});
};

export const getCustomLabel = id => {
  try {
    return localStorage[`label-${id}`]?.trim();
  }
  catch(e) {
    console.error(`Could not access local storage ${e}`);
  }
};

export const withProfiler = (WrappedComponent) => (props) => (<Profiler><WrappedComponent {...props} /></Profiler>);

// Wrap a component in this to debug when properties change
export function withPropsChecker(WrappedComponent, defaultName= "?") {
  return class PropsChecker extends Component {
    componentDidUpdate(lastProps) {
      Object.keys(lastProps)
        .filter(key => {
          return lastProps[key] !== this.props[key];
        })
        .forEach(key => {
          const name = WrappedComponent.displayName || WrappedComponent.name || defaultName;
          console.debug(`changed property "${key}" on ${name} from`, lastProps[key], 'to', this.props[key]);
        });
    }
    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
}

// extraFlavors: { [mimeType]: string } — additional clipboard flavors to attempt alongside text/plain.
// Custom web MIME types (prefixed "web ") are only supported in Chrome/Edge; other browsers silently drop them.
// If multi-flavor write fails, falls back to text/plain-only and calls onFallback (if provided).
export const copyToClipboard = (text, onSuccess = null, extraFlavors = null, onFallback = null) => {
  const writeToClipboard = (dataTypes) => {
    const item = new ClipboardItem(dataTypes);
    return navigator?.clipboard?.write([item]);
  };

  const dataTypes = {
    "text/plain": new Blob([text], {type: 'text/plain'}),
  };

  if (extraFlavors && Object.keys(extraFlavors).length > 0) {
    for (const [mime, value] of Object.entries(extraFlavors)) {
      dataTypes[mime] = new Blob([value], {type: mime});
    }
    writeToClipboard(dataTypes)
      .then(() => {
        if (onSuccess) onSuccess();
        else toast.success("Copied to clipboard");
      })
      .catch(() => {
        // Multi-flavor failed (unsupported browser) — fall back to text/plain only
        writeToClipboard({"text/plain": dataTypes["text/plain"]})
          .then(() => {
            if (onFallback) onFallback();
            else if (onSuccess) onSuccess();
            else toast.success("Copied to clipboard");
          })
          .catch((error) => {
            toast.error(`Clipboard write is unavailable (${error})`);
          });
      });
  } else {
    writeToClipboard(dataTypes)
      .then(() => {
        if (onSuccess) onSuccess();
        else toast.success("Copied to clipboard");
      })
      .catch((error) => {
        toast.error(`Clipboard write is unavailable (${error})`);
      });
  }
}

export const naturalSort = (a, b) => {
  if (!a) {
    return !b ? 0 : 1;
  }
  return a.localeCompare(b, undefined, {numeric: true, ignorePunctuation: true, sensitivity: "base"});
};

export const RE_PREFIXED = /^([^:]+):.*$/i;
export const RE_INT_ID = /^[^:]+:(\d+)$/i;
export const RE_DATASET_ID = /^([-a-z0-9]+):(.*)$/;

export const splitID = id => [datasetPrefix(id), getExternalID(id)];

export const stripDataset = id => RE_DATASET_ID.test(id) ? id.replace(RE_DATASET_ID, '$2') : id;

export const datasetPrefix = id => RE_PREFIXED.test(id) && id.replace(RE_PREFIXED, '$1');

export const isInteger = (s) => {
  return Number.isInteger(+(typeof s === 'string' ? s.trim() : s));
};

export const formatMultipleItemTitle = (items, maxItems = null) => {
  if (!maxItems) {
    maxItems = 3;
  }
  const names = items.map(x => typeof(x) === "string" ? x : getBestName(x, maxItems != null))
  if (items.length > maxItems + 1) {
    const explicit = names.slice(0, maxItems);
    return `${explicit.join(", ")} and ${items.length - explicit.length} others`;
  }
  return `${names.join(", ")}`;
}

// TODO: move title setting to Helmet (once helmet errors get fixed)
export const setPageTitle = (basis = "Plex Search", maxItems = 3) => {
  if (typeof(basis) === "string") {
    document.title = basis;
  }
  else if (basis?.length) {
    document.title = formatMultipleItemTitle(basis, maxItems)
  }
  else {
    document.title = "Plex Search";
  }
};

export function isTruthy(s) {
  return s === true || (typeof(s) === "string" && ["y", "yes", "true", "t"].includes(s.toLowerCase()));
}

export const isImage = file => {
  return /^image\//.test(file.type);
};
export const fileKey = file => {
  if (!file) {
    throw new TypeError(`File must be non-null`)
  }
  return `${file.name}:${file.size}:${file.lastModified}`;
};
export const areSetsEqual = (a, b) => a.size === b.size && [...a].every(value => b.has(value));

// Uploads that neither complete nor error within this window are killed via xhr.timeout —
// without it a stalled request never fires onload/onerror and the attachment hangs forever.
const UPLOAD_TIMEOUT_MS = 60000;

export const xhrUpload = async (url, file, onProgress = (uploadState) => {
}, authData = null) => {
  const state = {file, progress: 0, active: true};
  onProgress(state);
  return new Promise((resolve, reject) => {
    if (DEV && url.startsWith("/")) {
      url = `http://localhost:5000${url}`;
    }
    const formData = new FormData();
    formData.append('file', file);
    const xhr = new XMLHttpRequest();
    state.xhr = xhr;
    xhr.open("POST", url, true);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    Object.entries(authData.headers || {}).forEach(([k, v]) => {
      xhr.setRequestHeader(`${k}`, `${v}`);
    })

    const getResponse = xhr => {
      const raw = xhr.response;
      if (xhr.responseType === "" && typeof (raw) === "string" && (raw[0] === "{" || raw === "null")) {
        return ["json", JSON.parse(raw)];
      }
      return [xhr.responseType, raw];
    };

    onProgress(state);
    xhr.upload.onprogress = e => {
      state.event = e;
      if (e.lengthComputable) {
        state.progress = Math.floor(e.loaded * 100 / e.total);
      } else {
        state.progress += (e.total - state.progress) / 2;
      }
      onProgress(state);
    };
    xhr.onerror = (e) => {
      state.event = e;
      state.error = e;
      state.status = xhr.status;
      state.active = false;
      //console.log("xhr.onerror");
      reject(state);
    };
    xhr.ontimeout = (e) => {
      state.event = e;
      state.error = e;
      state.timeout = true;
      state.status = xhr.status;
      state.active = false;
      //console.log("xhr.ontimeout");
      reject(state);
    };
    xhr.onabort = (e) => {
      state.event = e;
      state.abort = true;
      state.active = false;
      //console.log("xhr.onabort");
      reject(state);
    };
    xhr.onload = e => {
      const [responseType, response] = getResponse(xhr);
      state.event = e;
      state.status = xhr.status;
      state.active = false;
      //console.log("xhr.onload", xhr, state);
      onProgress(state);
      if (xhr.readyState === xhr.DONE) {
        state.response = response;
        if (xhr.status && xhr.status < 400 && (responseType !== "json" || !response.status || response.status < 400)) {
          //console.log("xhr complete", xhr, state);
          resolve(state)
        } else {
          state.error = e;
          //console.log("xhr.onload(error)", xhr, state);
          reject(state);
        }
      }
    }
    xhr.send(formData);
  });
}

export const uploadFile = async (uploadURL, file, authData,
                                 onError = error => {},
                                 onProgress = (file, progress) => {},
                                 onComplete = (file, response) => {}) => {
    //console.log(`uploadFile: ${file.name}`);
    return xhrUpload(uploadURL, file, onProgress, authData)
      .then(state => {
        onComplete(file, state.response);
      })
      .catch(state => {
        //console.log("uploadFile.catch", state);
        if ((state.status === 401 || state.status === 403) && authData) {
          // Refresh credentials and retry
          //console.log("Refresh creds")
          return authData.refresh()
            .then(newAuthData => uploadFile(uploadURL, file, newAuthData, onError, onProgress, onComplete))
            .catch(error => {
              state.error = error;
              onError(state);
            })
        }
        else {
          onError(state);
        }
      });
  };

// Adjust legacy IDs to current ones
// Call this just before making an API call or otherwise generating a URL
export const updateLegacyId = (id) => {
  if (!RE_PLEX_ID.test(id)) {
    return id;
  }
  id = id
    .replace("entrez_gene:", "entrezgene:")
    .replace("open_targets:", "opentargets:")
    .replace("patent:", "patents:")
    .replace("company:", "orgs:");
  if (!legacyGS) {
    // remove gs- or cs- prefixes on non-custom datasets
    // NOTE: only do this if the dataset is 1.4.2+
    id = id.replace(/((ccle[a-z]+|gpsa|cdcode|moaa):)[cg]s-/, "$1")
      // old orcs, ccle, and gxa geneset IDs ONLY (not decrypte, moaa, or other datasets)
      .replace(/^(orcs|ccle[a-z]+|gxa):(.+):(up|dn|gain|loss)$/, "$1:$2|$3");
  }
  // Going from 1.4.2b (beta) to 1.4.2a (auto) will fail
  if (!legacyGPSA) {
      // Old-style GPSA genesets; changed in 1.4.2b+
      // Allow any non-underscore characters in both ID parts (handles slashes, etc.)
      id = id.replace(/^(gpsa:[^_]+)_[^_]+_UP$/, "$1|up")
        .replace(/^(gpsa:[^_]+)_[^_]+_DOWN$/, "$1|dn");
  }
  return id;
}

// Adjust legacy IDs to current ones
// Call this just before making an API call or otherwise generating a URL
export const updateLegacyIDs = ids =>ids.map(id => updateLegacyId(id));

export const hash = s => (s || "").split('').reduce((prev, curr) => Math.imul(31, prev) + curr.charCodeAt(0) | 0, 0);

export const abbreviateLines = (text, lines = 16) => {
  const split = text.split(/\n/);
  if (split.length > lines) {
    return `${split.slice(0, lines).join("\n")}...`;
  }
  return text;
};



// Taken from npm package content-disposition, with bugfixes applied
export const parseContentDisposition = (string) => {

  // RegExp to match percent encoding escape.
  const HEX_ESCAPE_REPLACE_REGEXP = /%([0-9A-Fa-f]{2})/g;
  // RegExp to match non-latin1 characters.
  const NON_LATIN1_REGEXP = /[^\x20-\x7e\xa0-\xff]/g;
  // RegExp to match quoted-pair in RFC 2616
  const QESC_REGEXP = /\\([\u0000-\u007f])/g;

  /**
   * RegExp for various RFC 2616 grammar
   *
   * parameter     = token "=" ( token | quoted-string )
   * token         = 1*<any CHAR except CTLs or separators>
   * separators    = "(" | ")" | "<" | ">" | "@"
   *               | "," | ";" | ":" | "\" | <">
   *               | "/" | "[" | "]" | "?" | "="
   *               | "{" | "}" | SP | HT
   * quoted-string = ( <"> *(qdtext | quoted-pair ) <"> )
   * qdtext        = <any TEXT except <">>
   * quoted-pair   = "\" CHAR
   * CHAR          = <any US-ASCII character (octets 0 - 127)>
   * TEXT          = <any OCTET except CTLs, but including LWS>
   * LWS           = [CRLF] 1*( SP | HT )
   * CRLF          = CR LF
   * CR            = <US-ASCII CR, carriage return (13)>
   * LF            = <US-ASCII LF, linefeed (10)>
   * SP            = <US-ASCII SP, space (32)>
   * HT            = <US-ASCII HT, horizontal-tab (9)>
   * CTL           = <any US-ASCII control character (octets 0 - 31) and DEL (127)>
   * OCTET         = <any 8-bit sequence of data>
   * @private
   */
  const PARAM_REGEXP = /;[\x09\x20]*([!#$%&'*+.0-9A-Z^_`a-z|~-]+)[\x09\x20]*\*?=[\x09\x20]*("(?:[\x20!\x23-\x5b\x5d-\x7e\x80-\xff]|\\[\x20-\x7e])*"|[()!#$%&'*+.0-9A-Z^_`a-z|~-]+)[\x09\x20]*/g;

  /**
   * RegExp for various RFC 5987 grammar
   *
   * ext-value     = charset  "'" [ language ] "'" value-chars
   * charset       = "UTF-8" / "ISO-8859-1" / mime-charset
   * mime-charset  = 1*mime-charsetc
   * mime-charsetc = ALPHA / DIGIT
   *               / "!" / "#" / "$" / "%" / "&"
   *               / "+" / "-" / "^" / "_" / "`"
   *               / "{" / "}" / "~"
   * language      = ( 2*3ALPHA [ extlang ] )
   *               / 4ALPHA
   *               / 5*8ALPHA
   * extlang       = *3( "-" 3ALPHA )
   * value-chars   = *( pct-encoded / attr-char )
   * pct-encoded   = "%" HEXDIG HEXDIG
   * attr-char     = ALPHA / DIGIT
   *               / "!" / "#" / "$" / "&" / "+" / "-" / "."
   *               / "^" / "_" / "`" / "|" / "~"
   * @private
   */
  const EXT_VALUE_REGEXP = /^([A-Za-z0-9!#$%&+\-^_`{}~]+)'(?:[A-Za-z]{2,3}(?:-[A-Za-z]{3}){0,3}|[A-Za-z]{4,8}|)'((?:%[0-9A-Fa-f]{2}|[()A-Za-z0-9!#$&+.^_`|~-])+)$/;

  /**
   * RegExp for various RFC 6266 grammar
   *
   * disposition-type = "inline" | "attachment" | disp-ext-type
   * disp-ext-type    = token
   * disposition-parm = filename-parm | disp-ext-parm
   * filename-parm    = "filename" "=" value
   *                  | "filename*" "=" ext-value
   * disp-ext-parm    = token "=" value
   *                  | ext-token "=" ext-value
   * ext-token        = <the characters in token, followed by "*">
   * @private
   */
  const DISPOSITION_TYPE_REGEXP = /^([!#$%&'*+.0-9A-Z^_`a-z|~-]+)[\x09\x20]*(?:$|;)/;

  /**
   * Decode a RFC 5987 field value (gracefully).
   *
   * @param {string} str
   * @return {string}
   * @private
   */

  const decodefield  = (str) => {
    const match = EXT_VALUE_REGEXP.exec(str.replace(/^"(.*)"$/, "$1"));

    if (!match) {
      throw new TypeError('invalid extended field value')
    }

    const charset = match[1].toLowerCase();
    const encoded = match[2];

    // to binary string
    const binary = encoded.replace(HEX_ESCAPE_REPLACE_REGEXP, pdecode);

    switch (charset) {
      case 'iso-8859-1':
        return getlatin1(binary);
      case 'utf-8':
      case 'utf8':
        return Buffer.from(binary, 'binary').toString('utf8');
      default:
        throw new TypeError('unsupported charset in extended field')
    }
  }

  /**
   * Get ISO-8859-1 version of string.
   *
   * @param {string} val
   * @return {string}
   * @private
   */

  const getlatin1 = (val) => {
    // simple Unicode -> ISO-8859-1 transformation
    return String(val).replace(NON_LATIN1_REGEXP, '?');
  }

  /**
   * Percent decode a single character.
   *
   * @param {string} str
   * @param {string} hex
   * @return {string}
   * @private
   */

  const pdecode = (str, hex)=> {
    return String.fromCharCode(parseInt(hex, 16));
  }

  /**
   * Percent encode a single character.
   *
   * @param {string} char
   * @return {string}
   * @private
   */
  const pencode = (char) => {
    return '%' + String(char)
      .charCodeAt(0)
      .toString(16)
      .toUpperCase();
  }

  if (!string || typeof string !== 'string') {
    throw new TypeError('argument string is required')
  }

  let match = DISPOSITION_TYPE_REGEXP.exec(string);

  if (!match) {
    throw new TypeError('invalid type format')
  }

  // normalize type
  let index = match[0].length;
  const type = match[1].toLowerCase();

  let key;
  const names = [];
  const params = {};
  let value;

  // calculate index to start at
  index = PARAM_REGEXP.lastIndex = match[0].slice(-1) === ';'
                                   ? index - 1
                                   : index;

  // match parameters
  while ((match = PARAM_REGEXP.exec(string))) {
    if (match.index !== index) {
      throw new TypeError('invalid parameter format')
    }

    index += match[0].length;
    key = match[1].toLowerCase();
    value = match[2];

    if (names.indexOf(key) !== -1) {
      throw new TypeError('invalid duplicate parameter')
    }

    names.push(key);

    if (key.indexOf('*') + 1 === key.length) {
      // decode extended value
      key = key.slice(0, -1);
      value = decodefield(value);

      // overwrite existing value
      params[key] = value;
      continue
    }

    if (typeof params[key] === 'string') {
      continue
    }

    if (value[0] === '"') {
      // remove quotes and escapes
      value = value
        .slice(1, -1)
        .replace(QESC_REGEXP, '$1');
    }

    params[key] = value;
  }

  if (index !== -1 && index !== string.length) {
    throw new TypeError('invalid parameter format');
  }

  return {type, parameters: params};
};

export const safeParseJSON = text => {
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
};
